import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCityNameById } from '../utils/cityUtils';
import {
  DocumentTextIcon,
  PhotoIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';


const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Статистика больше не загружается из API
    setLoading(false);
  }, []);

  const getRoleDisplayName = (type: string) => {
    switch (type) {
      case 'admin':
        return 'Администратор';
      case 'manager':
        return 'Менеджер';
      default:
        return 'Пользователь';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Заголовок */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-base-content mb-4">
          🚀 Дашборд
        </h1>
        <p className="text-lg font-medium text-base-content/70 mb-6">
          Добро пожаловать в систему управления агентами AgentB24
        </p>
        <div className="inline-flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-full px-6 py-3">
          <span className="text-lg">📅</span>
          <div className="text-sm text-primary font-semibold">
            {new Date().toLocaleDateString('ru-RU', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>
      </div>

      {/* Информация о пользователе */}
      <div className="card bg-primary/5 border border-primary/20">
        <div className="card-body">
          <div className="flex items-center space-x-6">
            <div className="avatar placeholder">
              <div className="bg-primary text-primary-content rounded-full w-16 h-16">
                <span className="text-2xl font-bold">{user?.login?.charAt(0).toUpperCase()}</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-base-content mb-2">
                {user?.login}
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="badge badge-primary">
                  {getRoleDisplayName(user?.type || '')}
                </div>
                {user?.category !== null && user?.category !== undefined && (
                  <div className="badge badge-secondary">
                    Город: {getCityNameById(user.category)}
                  </div>
                )}
              </div>
              {user?.sources && user.sources.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-base-content/70 block mb-1">Источники:</span>
                  <div className="flex flex-wrap gap-1">
                    {user.sources.map((s) => (
                      <span key={s.id} className="badge badge-outline badge-sm">
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Информация о системе */}
      <div className="card">
        <div className="card-body">
          <h3 className="card-title mb-6 flex items-center gap-3 text-lg font-bold text-base-content">
            <div className="w-6 h-6 bg-info rounded-lg flex items-center justify-center">
              <span className="text-info-content text-sm">ℹ️</span>
            </div>
            Информация о системе
          </h3>
          <div className="alert alert-info">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Данные загружаются напрямую из CRM в реальном времени. Статистика доступна на страницах сделок.</span>
          </div>
        </div>
      </div>

      {/* Быстрые действия */}
      <div className="card">
        <div className="card-body">
          <h3 className="card-title mb-6 flex items-center gap-3 text-lg font-bold text-base-content">
            <div className="w-6 h-6 bg-accent rounded-lg flex items-center justify-center">
              <span className="text-accent-content text-sm">⚡</span>
            </div>
            Быстрые действия
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <button className="btn btn-outline btn-primary group hover:scale-105 transition-transform">
              <DocumentTextIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
              Просмотр сделок
            </button>
            
            <button className="btn btn-outline btn-secondary group hover:scale-105 transition-transform">
              <PhotoIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
              Управление картинками
            </button>
            
            {user?.type === 'admin' && (
              <button className="btn btn-outline btn-accent group hover:scale-105 transition-transform">
                <CurrencyDollarIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                Администрирование
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
